import axios, { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query';
import { Profile } from '~/types/models';
import { DefaultGetAllResponse } from './rest';

/**
 * API Actions
 */

export const api = '/api/v2/profiles';

type GetProfilesResponse = DefaultGetAllResponse<Profile>;

export async function getProfiles({
  required = ['email'],
  search,
}: FilterProfileArgs) {
  const res = await axios.get<GetProfilesResponse>(api, {
    params: {
      query: search ?? undefined,
      required,
    },
  });

  return res.data.data;
}

export async function getProfile(uuid: string) {
  const res = await axios.get<Profile>(`${api}/${uuid}`);

  return res.data;
}

type CreateProfileArgs = Partial<Profile>;
type CreateProfilesResponse = {
  data: Profile;
};

export async function createProfile(attributes: CreateProfileArgs) {
  const res = await axios.post<CreateProfilesResponse>(api, attributes);

  return res.data.data;
}

type UpdateProfileParams = Partial<Profile>;
type UpdateProfileResponse = {
  data: Profile;
};

export async function updateProfile({ id, ...params }: UpdateProfileParams) {
  const res = await axios.put<UpdateProfileResponse>(
    `${api}/${id as string}`,
    params
  );

  return res.data.data;
}

export const queryKeys = {
  all: ['profiles'] as const,
  lists: () => [...queryKeys.all, 'lists'] as const,
  list: (filters: FilterProfileArgs) =>
    [...queryKeys.lists(), filters] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (id: string) => [...queryKeys.details(), id] as const,
};

/**
 * Custom Hooks
 */

type UseProfilesArgs = {
  required?: Array<keyof Profile>;
  search?: string;
  options?: UseQueryOptions<Profile[], AxiosError>;
};

type FilterProfileArgs = Omit<UseProfilesArgs, 'options'>;

export function useProfiles({
  required = ['email'],
  search,
  options,
}: UseProfilesArgs) {
  return useQuery<Profile[], AxiosError>(
    queryKeys.list({ required, search }),
    () => getProfiles({ required, search }),
    options
  );
}

type UseProfileParams = {
  uuid: string;
  options?: UseQueryOptions<Profile, AxiosError>;
};

export function useProfile({ uuid, options }: UseProfileParams) {
  return useQuery<Profile, AxiosError>(
    queryKeys.detail(uuid),
    () => getProfile(uuid),
    options
  );
}

export function useCreateProfile(
  options?: UseMutationOptions<Profile, AxiosError, CreateProfileArgs>
) {
  return useMutation(createProfile, options);
}

export function useUpdateProfile(
  options?: UseMutationOptions<Profile, AxiosError, UpdateProfileParams>
) {
  return useMutation(updateProfile, options);
}
